import React, { useEffect, useRef, useState } from "react";
import { Checkbox, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger } from "@fluentui/react-components";
import { Delete16Regular } from "@fluentui/react-icons";
import { Prz, RawMaterial } from "../redux/powderOrder";
import { usePowder } from "../redux/ReduxContext";
import PowderPieChart from "./PowderPieChart";
import { GetComposition, PowderComposition, PowderInventoryGroupedByDescription } from "../../../services/PowderFormService";
import CustomDropdown from "../CustomDropdown";
import { DecimalInput } from "../../DecimalInput";
import { toCanonicalDecimal } from "../../../utils/decimal";

interface PowderRawMaterialRowProps {
    index: number;
    totalRatioEnabled: boolean;
    pegRatioEnabled: boolean;
    recycledRatioEnabled: boolean;
    inventory: PowderInventoryGroupedByDescription[];
    onChangeLot: (index: number, nextLot: string) => void;
};

export const LotCell = ({ row, cellBackground, hasComposition }: { row: RawMaterial; cellBackground: () => string; hasComposition: boolean }) => {

    const content = (
        <div
            className={`h-full flex items-center justify-center text-primary-dark dark:text-primary-light text-xs overflow-hidden ${hasComposition ? 'cursor-pointer' : ''} ${cellBackground}`}
        >
            <span
                className={`bloc w-full truncate ml-4 text-left ${hasComposition ? 'underline' : ''}`}
                title={row.lot}
            >
                {row.lot}
            </span>
        </div>
    );

    if (!hasComposition) {
        return content;
    }
    return (
        <Dialog>
            <DialogTrigger disableButtonEnhancement>
                {content}
            </DialogTrigger>

            <DialogSurface>
                <DialogBody>
                    <DialogTitle>Lot Details</DialogTitle>
                    <DialogContent>
                        <PowderPieChart data={row.powderComposition} />
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
export const PowderRawMaterialRow: React.FC<PowderRawMaterialRowProps> = ({
    index,
    totalRatioEnabled,
    pegRatioEnabled,
    recycledRatioEnabled,
    inventory,
    onChangeLot,
}) => {
    const { state, dispatch } = usePowder();
    const lotTriggerRef = useRef<HTMLButtonElement>(null);
    const [isLotDropdownOpen, setIsLotDropdownOpen] = useState(false);
    const row = state.materials[index];
    const rowLot = row?.lot ?? "";
    const rowRvid = row?.rvid ?? "";
    const availableLots = inventory.find((item) => item.Description === row?.raw_mtrl)?.Lots ?? [];
    const selectedLotItem = availableLots.find((lotItem) => lotItem.Lot === rowLot);
    const availableTrimElement = selectedLotItem?.Trim ?? "";

    useEffect(() => {
        if (!rowLot || !rowRvid) {
            return;
        }

        let oxygenPercent = 0;
        let carbonPercent = 0;
        const powderComposition: PowderComposition[] = [];
        GetComposition(rowRvid, rowLot).then(composition => {
            if (composition) {
                powderComposition.push(...composition);
                composition.forEach(element => {
                    if (element.Symbol === "O") {
                        oxygenPercent = element.Percentage;
                    }
                    if (element.Symbol === "C") {
                        carbonPercent = element.Percentage;
                    }
                });

                dispatch({
                    type: 'UPDATE_ARRAY_ITEM', payload: {
                        field: 'materials', index: index, value: (prevItem: RawMaterial) => ({
                            ...prevItem,
                            powderComposition,
                            oPercent: oxygenPercent,
                            cPercent: carbonPercent,
                        })
                    }
                });
            }
        });

    }, [dispatch, index, rowLot, rowRvid])

    if (!row) {
        return null;
    }

    const cellBackground = () => {
        if (row.raw_mtrl?.includes("PRZ")) return "bg-primary-dark/20 dark:bg-primary-dark/50";
        if (row.raw_mtrl?.includes("PEG")) return "bg-primary-light dark:bg-primary-dark";

        return "bg-primary-light dark:bg-primary-dark";
    }
    const ratioInputClass = "focus:outline-none bg-primary-light dark:bg-primary-dark h-[32px] w-[68px] min-w-[68px] max-w-[68px] h-full text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold border px-0.5 sm:px-2 text-center dark:border-primary-light/50 border-primary-dark/50";
    return (
        <tr className="h-[48px] flex">
            <td className="w-8 flex-none">
                <div className="h-full w-7 items-center justify-center flex">
                    <button
                        type="button"
                        className="w-4 h-4 sm:w-6 sm:h-6 flex items-center justify-center text-primary-dark dark:text-primary-light hover:opacity-80"
                        aria-label="Delete row"
                        onClick={() => {
                            dispatch({ type: 'DELETE_ARRAY_ITEM', payload: { field: 'materials', index: index } })
                            if (row.raw_mtrl?.includes("PRZ")) {
                                const prz: Prz = {
                                    ...state.prz,
                                    enabled: false,
                                    selected_lot: null,
                                    ratio_pct: null,
                                };
                                dispatch({ type: "SET_FIELD", payload: { field: "prz", value: prz } })
                            }
                        }
                        }
                    >
                        <Delete16Regular className="w-4 h-4" />
                    </button>
                </div>
            </td>
            <td className={` w-[104px] min-w-[104px] sm:w-[180px] sm:min-w-[180px] flex-none `}>
                <div className={`h-full flex items-center px-2 text-primary-dark dark:text-primary-light text-xs truncate ${cellBackground()}`} title={row.raw_mtrl}>{row.raw_mtrl}</div>
            </td>
            <td className="flex-none w-[96px] min-w-[96px] max-w-[96px] sm:w-[168px] sm:min-w-[168px] sm:max-w-[168px] overflow-hidden">
                <div className={`h-full flex items-stretch ${cellBackground()}`}>
                    <div className="min-w-0 flex-1 overflow-hidden border-primary-dark/20 dark:border-primary-light/20">
                        <LotCell row={row} cellBackground={cellBackground} hasComposition={selectedLotItem?.HasComposition ?? false} />
                    </div>
                    <button
                        ref={lotTriggerRef}
                        type="button"
                        className="relative z-[10000] flex w-[36px] min-w-[36px] items-center justify-center"
                        aria-label={`Select lot for ${row.raw_mtrl}`}
                        onClick={() => setIsLotDropdownOpen(!isLotDropdownOpen)}
                    >
                        <svg
                            className="w-4 h-4 text-primary-dark dark:text-primary-light"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                    <CustomDropdown
                        value={rowLot}
                        options={
                            inventory
                                .find((item) => item.Description === row?.raw_mtrl)
                                ?.Lots?.map((lotItem) => ({
                                    label: lotItem.Lot,
                                    value: lotItem.Lot,
                                    HasComposition: lotItem.HasComposition,
                                })) ?? []
                        }
                        onChange={(val) => onChangeLot(index, val)}
                        open={isLotDropdownOpen}
                        onOpenChange={setIsLotDropdownOpen}
                        targetRef={lotTriggerRef}
                        surfaceMinWidth={168}
                    />
                </div>
            </td>
            <td className="w-[32px]">
                <div className={`h-full flex items-center justify-center ${cellBackground()}`}>
                    {availableTrimElement !== "" && (
                        <Checkbox
                            checked={state.materials[index || 0]?.role === "trim"}
                            onChange={(e) => {
                                const newRole = e.target.checked ? "trim" : "main";
                                dispatch({
                                    type: 'UPDATE_ARRAY_ITEM', payload: {
                                        field: 'materials', index: index || 0, value: (prevItem: RawMaterial) => ({
                                            ...prevItem,
                                            role: newRole === "trim" ? "trim" : "main",
                                            trim_element: newRole === "trim" ? availableTrimElement : "",
                                        })
                                    }
                                })
                            }
                            }

                            className="[&_.fui-Checkbox__indicator]:border-[#ffbf39] [&_.fui-Checkbox__indicator]:text-[#ffbf39]"
                            aria-label={`Mark ${row.raw_mtrl} ${row.lot} as trim`}
                        />
                    )}


                </div>
            </td>


            {totalRatioEnabled ? (
                <td className="w-[96px] min-w-[96px] max-w-[96px]">
                    <div className={`h-full flex items-center justify-center p-1 ${cellBackground()}`}>
                        {
                            state.materials.filter(m => m.raw_mtrl === row.raw_mtrl).length > 1 ? (
                                <DecimalInput
                                    pattern="^[0-9]*[,.]?[0-9]*$"
                                    className={ratioInputClass}
                                    value={row.ratio}
                                    onValueChange={value => dispatch({
                                        type: 'UPDATE_ARRAY_ITEM', payload: {
                                            field: 'materials', index: index || 0, value: (prevItem: RawMaterial) => ({
                                                ...prevItem,
                                                ratio: toCanonicalDecimal(value)
                                            })
                                        }
                                    })}
                                    placeholder="-"
                                />
                            ) : <span className="text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold">-</span>}
                    </div>
                </td>
            ) : null}

            {pegRatioEnabled ? (
                <td className="w-[96px] min-w-[96px] max-w-[96px]">
                    <div className={`h-full flex items-center justify-center p-1 ${cellBackground()}`}>
                        {row.raw_mtrl?.includes("PEG") ? (
                            <DecimalInput
                                pattern="^[0-9]*[,.]?[0-9]*$"
                                className={ratioInputClass}
                                value={row.pegRatio}
                                onValueChange={value => {
                                    dispatch({
                                        type: 'UPDATE_ARRAY_ITEM', payload: {
                                            field: 'materials', index: index || 0, value: (prevItem: RawMaterial) => ({
                                                ...prevItem,
                                                pegRatio: toCanonicalDecimal(value)
                                            })
                                        }
                                    })
                                }
                                }
                                placeholder="-"
                            />
                        ) : <span className="text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold">-</span>}
                    </div>
                </td>
            ) : null}

            {recycledRatioEnabled ? (
                <td className="w-[96px] min-w-[96px] max-w-[96px]">
                    <div className={`h-full flex items-center justify-center p-1 ${cellBackground()}`}>
                        {row.raw_mtrl?.includes("PRZ") ? (
                            <DecimalInput
                                pattern="^[0-9]*[,.]?[0-9]*$"
                                className={ratioInputClass}
                                value={row.recycledRatio}
                                onValueChange={value => {
                                    dispatch({
                                        type: 'UPDATE_ARRAY_ITEM', payload: {
                                            field: 'materials', index: index || 0, value: (prevItem: RawMaterial) => ({
                                                ...prevItem,
                                                recycledRatio: toCanonicalDecimal(value)
                                            })
                                        }
                                    }
                                    )
                                    const prz: Prz = {
                                        ...state.prz,
                                        ratio_pct: Number(toCanonicalDecimal(value))
                                    };
                                    dispatch({ type: "SET_FIELD", payload: { field: "prz", value: prz } })
                                }

                                }
                                placeholder="-"
                            />
                        ) : <span className="text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold">-</span>}
                    </div>
                </td>
            ) : null}
            {state.selectedElements?.map((el) => {

                const value = state.materials[index].contributionPercent?.[el];

                const display =
                    value != null && value !== 0
                        ? Math.abs(value) < 0.0001
                            ? value.toExponential(2).replace(".", ",") // scientific
                            : value.toFixed(4).replace(".", ",")       // normal
                        : '-';
                return (
                    <td key={el} className="h-full min-w-[84px]" title={value?.toString()}>
                        <div className={`h-full flex items-center justify-center ${cellBackground()}`}>
                            <span className="text-oi-button-blue dark:text-oi-powder-form-yellow light:text-primary-dark text-xs truncate">
                                {display}
                            </span>
                        </div>
                    </td>
                );
            })}
            {state.showImpurityExpansion && state.responseElements?.map((el) => {
                const value = state.materials[index].impurityExpansion
                    ?.find(item => item.element === el)
                    ?.contribution_to_batch_pct;

                const display =
                    value != null && value !== 0
                        ? Math.abs(value) < 0.0001
                            ? value.toExponential(2).replace(".", ",") // scientific
                            : value.toFixed(4).replace(".", ",")       // normal
                        : '-';

                return (
                    <td key={el} className="h-full min-w-[84px]" title={value?.toString()}>
                        <div className={`h-full flex items-center justify-center ${cellBackground()}`}>
                            <span className="text-oi-button-blue dark:text-oi-powder-form-yellow light:text-primary-dark text-xs truncate">
                                {display}
                            </span>
                        </div>
                    </td>
                );
            })}

        </tr>
    );
};
